import * as Dialog from "@radix-ui/react-dialog";
import { useState } from "react";
import { createWorkspace } from "../lib/api/index.js";
import { BusyLabel } from "./loader/index.js";
import { PixelDrop } from "./pixel-art/pixel-drop.js";
import { staysOpenForToasts, useToast } from "./toast.js";

/**
 * Naming a new workspace (DESIGN.md §9, the workspace switcher).
 *
 * Only ever on request: every workspace can track questions, and every
 * capture costs money, so none is made for anyone who did not ask.
 */
export function NewWorkspace({
  onMade,
  onClose,
}: {
  /** Called with the new workspace once it exists, to open it. */
  readonly onMade: (workspaceId: string) => Promise<void>;
  readonly onClose: () => void;
}) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const submit = async (): Promise<void> => {
    setBusy(true);
    try {
      const made = await createWorkspace(name.trim());
      await onMade(made.id);
      toast.done(`Workspace ${made.name} created`);
    } catch (error) {
      toast.failed("Could not create the workspace", error);
      setBusy(false);
    }
  };

  return (
    <Dialog.Root
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="drawer-scrim" />
        <Dialog.Content
          onInteractOutside={staysOpenForToasts}
          className="modal"
          aria-describedby="new-workspace-hint"
        >
          <PixelDrop
            shape="horizon"
            clear=".modal-title"
            until="button"
            down=".modal-title"
          />
          <Dialog.Title className="modal-title">New workspace</Dialog.Title>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (!busy) void submit();
            }}
          >
            <label className="field-label" htmlFor="new-workspace-name">
              Name
            </label>
            <input
              id="new-workspace-name"
              className="text-input"
              required
              autoFocus
              maxLength={120}
              placeholder="Acme marketing"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
            <p className="field-hint" id="new-workspace-hint">
              Yours to own, separate from the others: its own brands, questions and
              people. It opens on setting up its first brand.
            </p>
            <div className="research-actions">
              <Dialog.Close className="button-secondary">Cancel</Dialog.Close>
              <button type="submit" className="button-primary" disabled={busy}>
                <BusyLabel busy={busy} idle="Create workspace" working="Creating…" />
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
